import React, { useEffect, useState, useMemo } from "react";
import { useAuth } from "../../context/AuthContext";
import { organizationsAPI } from "../../utils/organizationsAPI";
import { showToast } from "../../components/Toast";
import { useTranslation } from "react-i18next";

function Support() {
  const { user } = useAuth();
  const [organization, setOrganization] = useState(null);
  const [analysts, setAnalysts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();

  const effectiveOrgId = useMemo(() => {
    if (user?.organization && typeof user.organization === "object") {
      return user.organization.id;
    }
    return user?.organization || null;
  }, [user]);

  useEffect(() => {
    const load = async () => {
      if (!effectiveOrgId) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const orgResp = await organizationsAPI.retrieve(effectiveOrgId);
        const orgData = orgResp?.data || orgResp;
        setOrganization(orgData || null);

        if (orgData?.assigned_analysts && Array.isArray(orgData.assigned_analysts)) {
          setAnalysts(orgData.assigned_analysts);
        } else {
          setAnalysts([]);
        }
      } catch (err) {
        showToast.error(err.message || "Failed to load support information");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [effectiveOrgId]);

  return (
    <div className="page-content">
      <div className="card">
        <h2 className="card-title">{t("support.support")}</h2>
        <p style={{ color: "var(--text-grey)", marginTop: 4, marginBottom: 16 }}>
          {t("support.questionsAboutData")}
        </p>
        {loading ? (
          <p>{t("support.loadingInfo", "Loading support information...")}</p>
        ) : !effectiveOrgId ? (
          <p>
            {t(
              "support.noOrganization",
              "No organization is linked to your account yet. Please contact the DSTL administrator for assistance.",
            )}
          </p>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 1.3fr) minmax(0, 1.1fr)",
              gap: 24,
              alignItems: "flex-start",
            }}
          >
            <div>
              <h3 style={{ marginBottom: 8 }}>{t("support.platformSupport")}</h3>
              <p
                style={{
                  margin: "4px 0",
                  fontSize: 14,
                  color: "var(--text-grey)",
                }}
              >
                {t(
                  "support.accountAccessIssues",
                  "For account access issues, password problems, or general questions about the DSTL platform, contact the central support team:",
                )}
              </p>
              <div style={{ fontSize: 14 }}>
                <p style={{ margin: 0 }}>
                  <strong>Email:</strong> dataspacetechonologies4@gmail.com
                </p>
                <p style={{ margin: 0 }}>
                  <strong>Phone:</strong> +250791837351
                </p>
              </div>

              <h3 style={{ margin: "20px 0 8px" }}>
                {t("support.organizationContacts", "Organization contacts")}
              </h3>
              <p style={{ margin: 0, fontWeight: 600 }}>
                {organization?.name || "—"}
              </p>
              <p
                style={{
                  margin: "4px 0",
                  fontSize: 14,
                  color: "var(--text-grey)",
                }}
              >
                {t(
                  "support.organizationHelpText",
                  "Use these contacts for company-specific questions, onboarding, or billing.",
                )}
              </p>
              <div style={{ fontSize: 14 }}>
                {organization?.contact_email && (
                  <p style={{ margin: 0 }}>
                    <strong>Email:</strong> {organization.contact_email}
                  </p>
                )}
                {organization?.contact_phone && (
                  <p style={{ margin: 0 }}>
                    <strong>Phone:</strong> {organization.contact_phone}
                  </p>
                )}
              </div>
            </div>

            <div>
              <h3 style={{ marginBottom: 8 }}>
                {t("platform.assignedAnalyst")}
              </h3>
              {analysts.length === 0 ? (
                <p style={{ margin: 0 }}>
                  {t(
                    "support.noAnalystAssigned",
                    "No analyst has been assigned to your organization yet. New reports and advanced analysis will be available once an analyst is linked.",
                  )}
                </p>
              ) : (
                <>
                  <p
                    style={{
                      margin: "4px 0 8px",
                      fontSize: 14,
                      color: "var(--text-grey)",
                    }}
                  >
                    {t(
                      "support.analystHelpText",
                      "Contact your analyst for help interpreting reports, understanding trends, or requesting a new analysis.",
                    )}
                  </p>
                  <ul style={{ paddingLeft: 20, margin: 0 }}>
                    {analysts.map((a) => (
                      <li key={a.id} style={{ marginBottom: 8, fontSize: 14 }}>
                        <strong>
                          {a.first_name} {a.last_name}
                        </strong>{" "}
                        ({a.email})
                        {a.phone && <> • Phone: {a.phone}</>}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Support;

